import { inject, observer } from "mobx-react";
import { FC, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import ClientManagementStore from "../../../modules/Admin/ClientManagement/store";
import { ClientCategoryDto } from "../../../modules/Admin/ClientManagement/types";
import { MessageResultState } from "../../../types/general-types";
import {
  CATEGORY_ICON_KEYS,
  CategoryIconKey,
  DEFAULT_CATEGORY_ID,
  FALLBACK_ICON,
  getCategoryName,
  iconKeyOf,
} from "../../config/categories";
import { REDESIGN_NS } from "../../i18n";
import useAutoTranslate from "../../lib/useAutoTranslate";
import Drawer from "../../ui/Drawer";
import { CategoryIcon, IconPencil, IconPlus } from "../../ui/icons";
import { ConfirmModal } from "../../ui/Modal";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  categories: ClientCategoryDto[];
  /** `portalsMoved`: yalnızca pasife almada portaller varsayılan kategoriye taşınır. */
  onChanged: (portalsMoved: boolean) => void;
  ClientManagementStore?: ClientManagementStore;
}

interface FormState {
  id?: number;
  name: string;
  name_EN: string;
  icon: CategoryIconKey;
  orderIndex: number;
}

const EMPTY_FORM: FormState = {
  name: "",
  name_EN: "",
  icon: FALLBACK_ICON,
  orderIndex: 1,
};

/**
 * Kategori yönetimi. Portal formu açık kalır ki kullanıcı kategoriyi ekleyip aynı formda
 * seçebilsin. Silme yok, pasife alma var: pasife alınan kategorinin portalleri varsayılana
 * taşınır ve bu geri alınamaz, onay ekranında kaç portalin etkileneceği yazılı.
 */
const CategoryDrawer: FC<Props> = inject("ClientManagementStore")(
  observer(
    ({
      isOpen,
      onClose,
      categories,
      onChanged,
      ClientManagementStore: store,
    }: Props) => {
      const { t, i18n } = useTranslation(REDESIGN_NS);

      const [form, setForm] = useState<FormState | null>(null);
      const [isBusy, setIsBusy] = useState(false);
      const [pendingDeactivate, setPendingDeactivate] =
        useState<ClientCategoryDto>();

      const { register, handleSubmit, errors, reset } = useForm();
      const scheduleTranslate = useAutoTranslate(store);

      const set = (patch: Partial<FormState>) =>
        setForm((current) => (current ? { ...current, ...patch } : current));

      const openForm = (next: FormState) => {
        reset();
        setForm(next);
      };

      const closeForm = () => {
        reset();
        setForm(null);
      };

      const save = handleSubmit(async () => {
        if (!form || !store) return;

        setIsBusy(true);
        try {
          const payload = {
            id: form.id,
            name: form.name.trim(),
            name_EN: form.name_EN.trim(),
            icon: form.icon,
            orderIndex: Number(form.orderIndex),
          };
          const response = form.id
            ? await store.updateCategory(payload)
            : await store.createCategory(payload);

          // Hata toast'ını interceptor basıyor; form açık kalır ki veri kaybolmasın.
          if (response?.state === MessageResultState.SUCCESS) {
            closeForm();
            onChanged(false);
          }
        } finally {
          setIsBusy(false);
        }
      });

      const applyActive = async (category: ClientCategoryDto, next: boolean) => {
        if (!store) return;
        setIsBusy(true);
        try {
          const response = await store.setCategoryActive(category.id, next);
          if (response?.state === MessageResultState.SUCCESS)
            onChanged(Number(response.result) > 0);
        } finally {
          setIsBusy(false);
          setPendingDeactivate(undefined);
        }
      };

      const toggleActive = (category: ClientCategoryDto) => {
        // Portal taşıma geri alınamaz; yalnızca pasife alma onay ister.
        if (category.isActive) setPendingDeactivate(category);
        else applyActive(category, true);
      };

      // Yeni kategori sona eklenir; 0 ya da negatif bir sira listeyi bastan bozardi.
      const nextOrderIndex =
        categories.reduce((top, category) => Math.max(top, category.orderIndex), 0) + 1;

      // Varsayılan kategorinin adı ve sırası sabit; backend de bu alanları yok sayıyor.
      const isDefaultCategory = form?.id === DEFAULT_CATEGORY_ID;

      const required = t("Please fill out this field.").toString();

      const minOrder = {
        value: 1,
        message: t("Must be 1 or greater.").toString(),
      };

      const invalid = (name: string) => (errors[name] ? " is-invalid" : "");

      const error = (name: string) =>
        errors[name] ? (
          <div className="sso-rd__error">{errors[name].message}</div>
        ) : null;

      return (
        <>
          <Drawer isOpen={isOpen} title={t("Categories")} onClose={onClose}>
            <div className="sso-rd__drawer-actions">
              <button
                type="button"
                className="sso-rd__btn sso-rd__btn--primary"
                onClick={() => openForm({ ...EMPTY_FORM, orderIndex: nextOrderIndex })}
                disabled={!!form}
              >
                <IconPlus size={16} />
                {t("New Category")}
              </button>
            </div>

            {form && (
              <div className="sso-rd__cat-form">
                <div className="sso-rd__field">
                  <label className="sso-rd__label" htmlFor="sso-rd-cat-name">
                    {t("Category Name")}
                    <span className="sso-rd__req">*</span>
                  </label>
                  <input
                    id="sso-rd-cat-name"
                    name="name"
                    type="text"
                    disabled={isDefaultCategory}
                    className={"sso-rd__input" + invalid("name")}
                    value={form.name}
                    ref={register(isDefaultCategory ? {} : { required })}
                    onChange={(event) => {
                      set({ name: event.target.value });
                      scheduleTranslate(event.target.value, (text) =>
                        set({ name_EN: text })
                      );
                    }}
                  />
                  {error("name")}
                </div>

                <div className="sso-rd__field">
                  <label className="sso-rd__label" htmlFor="sso-rd-cat-name-en">
                    {t("Category Name (EN)")}
                    <span className="sso-rd__req">*</span>
                  </label>
                  <input
                    id="sso-rd-cat-name-en"
                    name="name_EN"
                    type="text"
                    disabled={isDefaultCategory}
                    className={"sso-rd__input" + invalid("name_EN")}
                    value={form.name_EN}
                    ref={register(isDefaultCategory ? {} : { required })}
                    onChange={(event) => set({ name_EN: event.target.value })}
                  />
                  {error("name_EN")}
                </div>

                <div className="sso-rd__field">
                  <span className="sso-rd__label">{t("Icon")}</span>
                  <div className="sso-rd__icon-picker">
                    {CATEGORY_ICON_KEYS.map((key) => (
                      <button
                        key={key}
                        type="button"
                        className={
                          "sso-rd__icon-choice" +
                          (form.icon === key ? " is-active" : "")
                        }
                        onClick={() => set({ icon: key })}
                        aria-label={key}
                        aria-pressed={form.icon === key}
                      >
                        <CategoryIcon icon={key} size={19} />
                      </button>
                    ))}
                  </div>
                </div>

                <div className="sso-rd__field">
                  <label className="sso-rd__label" htmlFor="sso-rd-cat-order">
                    {t("Appearance Order")}
                    <span className="sso-rd__req">*</span>
                  </label>
                  <input
                    id="sso-rd-cat-order"
                    name="orderIndex"
                    type="number"
                    min={1}
                    step={1}
                    disabled={isDefaultCategory}
                    className={"sso-rd__input" + invalid("orderIndex")}
                    value={form.orderIndex}
                    ref={register(
                      isDefaultCategory ? {} : { required, min: minOrder }
                    )}
                    onChange={(event) =>
                      set({ orderIndex: Number(event.target.value) })
                    }
                  />
                  {error("orderIndex")}
                </div>

                <div className="sso-rd__modal-foot">
                  <button
                    type="button"
                    className="sso-rd__btn sso-rd__btn--modal sso-rd__btn--secondary"
                    onClick={closeForm}
                  >
                    {t("Close")}
                  </button>
                  <button
                    type="button"
                    className={
                      "sso-rd__btn sso-rd__btn--modal sso-rd__btn--primary" +
                      (isBusy ? " is-busy" : "")
                    }
                    onClick={save}
                    disabled={isBusy}
                    aria-busy={isBusy}
                  >
                    {isBusy && (
                      <span className="sso-rd__spinner" aria-hidden="true" />
                    )}
                    {t("Save")}
                  </button>
                </div>
              </div>
            )}

            {categories.map((category) => (
              <div
                key={category.id}
                className={
                  "sso-rd__cat-row" + (category.isActive ? "" : " is-passive")
                }
              >
                <span className="sso-rd__icon-tile">
                  <CategoryIcon icon={iconKeyOf(category)} size={18} />
                </span>

                <div className="sso-rd__cat-row-text">
                  <div className="sso-rd__cat-row-name">
                    {getCategoryName(category, i18n.language)}
                  </div>
                  <div className="sso-rd__cat-row-meta">
                    {t("{{n}} portal", { n: category.clientCount })}
                    {!category.isActive && " · " + t("Passive")}
                  </div>
                </div>

                <button
                  type="button"
                  className="sso-rd__icon-btn sso-rd__icon-btn--sm sso-rd__icon-btn--neutral"
                  onClick={() =>
                    openForm({
                      id: category.id,
                      name: category.name,
                      name_EN: category.name_EN,
                      icon: iconKeyOf(category),
                      orderIndex: category.orderIndex,
                    })
                  }
                  title={t("Edit")}
                  aria-label={t("Edit")}
                >
                  <IconPencil size={15} />
                </button>

                {/* Varsayılan kategori pasife alınamaz: taşınan portallerin
                    düştüğü yer orası. */}
                {category.id !== DEFAULT_CATEGORY_ID && (
                  <button
                    type="button"
                    className="sso-rd__btn sso-rd__btn--secondary sso-rd__btn--modal"
                    onClick={() => toggleActive(category)}
                    disabled={isBusy}
                  >
                    {category.isActive ? t("Deactivate") : t("Activate")}
                  </button>
                )}
              </div>
            ))}
          </Drawer>

          <ConfirmModal
            isOpen={!!pendingDeactivate}
            onClose={() => setPendingDeactivate(undefined)}
            onConfirm={() =>
              pendingDeactivate && applyActive(pendingDeactivate, false)
            }
            isBusy={isBusy}
            message={t(
              "The category will be deactivated and its {{n}} portals will move to the default category. This cannot be undone.",
              { n: pendingDeactivate?.clientCount ?? 0 }
            )}
          />
        </>
      );
    }
  )
);

export default CategoryDrawer;
